/**
 * EFFET : un détecteur MediaPipe (visage ou main) qui tourne dans un Web Worker quand le
 * navigateur le permet, sinon sur le fil principal. Même contrat dans les deux cas : des
 * pixels RGBA entrent (transférés), le résultat brut et les pixels reviennent.
 */
import type { Delegate } from './delegate';
import { describeError, type FromWorker, type Model, type ToWorker } from './landmarkerProtocol';
import { createLandmarker, type Landmarker } from './mediapipe';

export type DetectorResult<M extends Model> = ReturnType<Landmarker<M>['detectForVideo']>;

export interface Detection<M extends Model> {
  readonly result: DetectorResult<M>;
  /** Les pixels envoyés, rendus (le tampon transféré n'est plus lisible côté appelant avant). */
  readonly pixels: ArrayBuffer;
  /** Durée de l'inférence MediaPipe seule (ms) : le reste de l'aller-retour, c'est le transport. */
  readonly inferenceMs: number;
}

export interface Detector<M extends Model> {
  readonly where: 'worker' | 'main';
  detect(pixels: ArrayBuffer, width: number, height: number, t: number): Promise<Detection<M>>;
  close(): void;
}

/** Ce que le détecteur attend d'un `Worker` (structurellement : `Worker`). */
export interface WorkerLike {
  postMessage(message: ToWorker, transfer?: Transferable[]): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<FromWorker>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}

export interface DetectorOptions {
  /** Fabrique du worker ; `null` : fil principal. Défaut : un worker si le navigateur le permet. */
  readonly spawn?: (() => WorkerLike) | null;
  readonly createLandmarker?: typeof createLandmarker;
}

const workerSupported = (): boolean =>
  typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined';

/**
 * Worker MODULE : c'est ainsi que le serveur de dev le sert ; en production, Vite en fait un
 * script autonome que le navigateur charge aussi bien comme module. Dans les deux cas,
 * `importScripts` refuse et MediaPipe passe par `self.import` (voir `landmarker.worker.ts`).
 */
const defaultSpawn = (): (() => WorkerLike) | null =>
  workerSupported()
    ? () => new Worker(new URL('./landmarker.worker.ts', import.meta.url), { type: 'module' })
    : null;

export function createWorkerDetector<M extends Model>(
  model: M,
  delegate: Delegate,
  spawn: () => WorkerLike,
): Promise<Detector<M>> {
  const worker = spawn();
  const pending = new Map<
    number,
    { resolve: (d: Detection<M>) => void; reject: (e: Error) => void }
  >();
  let nextId = 0;
  let dead: Error | null = null;

  const fail = (error: Error): void => {
    dead = error;
    for (const p of pending.values()) p.reject(error);
    pending.clear();
    worker.terminate();
  };

  return new Promise<Detector<M>>((resolveReady, rejectReady) => {
    worker.onerror = (event) => {
      const error = new Error(`Worker MediaPipe : ${event.message || 'erreur'}`);
      rejectReady(error);
      fail(error);
    };
    worker.onmessage = ({ data }) => {
      switch (data.type) {
        case 'ready':
          resolveReady(detector);
          return;
        case 'init-error': {
          const error = new Error(data.message);
          rejectReady(error);
          fail(error);
          return;
        }
        case 'result':
          pending.get(data.id)?.resolve({
            result: data.result as DetectorResult<M>,
            pixels: data.pixels,
            inferenceMs: data.inferenceMs,
          });
          pending.delete(data.id);
          return;
        case 'detect-error':
          pending.get(data.id)?.reject(new Error(data.message));
          pending.delete(data.id);
          return;
      }
    };
    const detector: Detector<M> = {
      where: 'worker',
      detect(pixels, width, height, t) {
        if (dead) return Promise.reject(dead);
        const id = nextId++;
        return new Promise((resolve, reject) => {
          pending.set(id, { resolve, reject });
          worker.postMessage({ type: 'detect', id, pixels, width, height, t }, [pixels]);
        });
      },
      close() {
        fail(new Error('Détecteur fermé'));
      },
    };
    worker.postMessage({ type: 'init', model, delegate });
  });
}

export async function createMainThreadDetector<M extends Model>(
  model: M,
  delegate: Delegate,
  create: typeof createLandmarker = createLandmarker,
): Promise<Detector<M>> {
  const landmarker = await create(model, delegate);
  return {
    where: 'main',
    detect: (pixels, width, height, t) =>
      new Promise((resolve) => {
        const image = new ImageData(new Uint8ClampedArray(pixels), width, height);
        const before = performance.now();
        const result = landmarker.detectForVideo(image, t) as DetectorResult<M>;
        resolve({ result, pixels, inferenceMs: performance.now() - before });
      }),
    close: () => {
      landmarker.close();
    },
  };
}

/** Worker si possible ; sinon (navigateur sans worker WebGL, worker en échec) fil principal. */
export async function createDetector<M extends Model>(
  model: M,
  delegate: Delegate,
  { spawn = defaultSpawn(), createLandmarker: create = createLandmarker }: DetectorOptions = {},
): Promise<Detector<M>> {
  if (spawn) {
    try {
      return await createWorkerDetector(model, delegate, spawn);
    } catch (e) {
      console.warn(`MediaPipe (${model}) : worker indisponible, fil principal.`, describeError(e));
    }
  }
  return createMainThreadDetector(model, delegate, create);
}
